import { useMutation } from '@apollo/client'
import { Button, Form, Modal, Row, Space } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import TextArea from 'antd/lib/input/TextArea'
import { DEMANDES_A_TRAITER_QUERY, EVENEMENTS } from '../../apollo/query/demande.query'
import { EVENEMENT_ADD_MUTATION } from '../../apollo/mutation/evenement.mutation'
import { D4_Demande, D4_Demande_Set_Input, D4_Evenementdemande_Set_Input, D4_Fichier_Insert_Input } from '../../types'
import { FICHIER_MUTATION } from '../../apollo/mutation/fichier.mutation'
import { delay } from 'lodash'
import D4UploadFile from '../D4UploadFile'
import { DEMANDE_UPDATE } from '../../apollo/mutation/demande.mutation'
import { useAuth } from '../../lib/hooks'
import { useState } from 'react'
import Spinner from '../Spinner'

// POST ai/message
// body { input: { text: string; demandeId: number } }

function markdownToHTML(text: string) {
  // Convertir les marqueurs de gras (**texte**) en balises HTML <b></b>
  let html = text.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');

  // les sauts de ligne sont remplacés par des balises <br>
  html = html.replace(/\n/g, '<br>');

  // Ajouter d'autres règles de conversion ici si nécessaire

  return html;
}


const GPTButton = ({ demandeId }: { demandeId: number }) => {
  const [loading, setLoading] = useState(false)
  const [res, setRes] = useState<{ date: string, text: string }[]>([])

  const [text, setText] = useState('')
  const [visible, setVisible] = useState(false)

  const handleSend = async () => {
    setLoading(true)
    const completion = await fetch(`${process.env.REACT_APP_VISION_API}/ai/message`, {
      method: 'POST',
      body: JSON.stringify({ input: { text, demandeId } }),
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('jwt'),
        'Content-Type': 'application/json',
      },
    }).then((res) => {
      if (!res.ok) {
        throw new Error('Network response was not ok')
      }
      return res.text()
    })

    setLoading(false)
    /* format the date with dayjs d/m h:m:s */
    const date = new Date().toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    })
    setRes([{ date, text: completion }, ...res])

    return res
  }


  const handleClick = async () => {
    setVisible(true)
  }

  return (
    <>
      <Modal title="GPT" visible={visible}
        onOk={() => setVisible(false)}
        onCancel={() => setVisible(false)}
      >
        <TextArea rows={2} onChange={(e) => setText(e.target.value)} />
        <Button type="primary" onClick={handleSend}>Toudoum</Button>
        {
          loading && <Spinner />
        }
        {
          res.map((r) => (
            <div>
              <p>{r.date}</p>
              <p dangerouslySetInnerHTML={{ __html: markdownToHTML(r.text) }} />
            </div>
          ))
        }
      </Modal>

      <Button type="default" onClick={handleClick} loading={loading}>
        GPT
      </Button>
    </>
  )
}

const DemandeContentEditor = ({
  demandeId,
  onOpenFilesDrawer,
  onOpenProcedure,
  procedure,
}: {
  demandeId: number
  onOpenFilesDrawer: () => void
  onOpenProcedure: () => void
  procedure: boolean
}) => {
  const {
    user: {
      personne: { id },
    },
  } = useAuth()
  const [form] = useForm()
  const [uploadFile] = useMutation<{}, { data: D4_Fichier_Insert_Input }>(FICHIER_MUTATION)
  const [updateDemande] = useMutation<{ demande: D4_Demande }, { id: number; data: D4_Demande_Set_Input }>(
    DEMANDE_UPDATE
  )
  const [createEvenement, { loading }] = useMutation<{}, { data: D4_Evenementdemande_Set_Input }>(
    EVENEMENT_ADD_MUTATION,
    {
      refetchQueries: [
        {
          query: EVENEMENTS,
          variables: { id: Number(demandeId) },
        },
      ],
      fetchPolicy: 'no-cache',
    }
  )

  const handleOnOk = async ({ nomfichier, nomstockage, auteur_id }: UploadFileResult) => {
    await delay(() => {
      uploadFile({
        variables: {
          data: {
            demande_id: demandeId,
            dtype: 'FichierDemande',
            nomfichier,
            nomstockage,
            auteur_id,
          },
        },
        refetchQueries: [
          {
            query: EVENEMENTS,
            variables: { id: Number(demandeId) },
          },
        ],
      })
      updateDemande({
        variables: { id: demandeId, data: { datedernierimportmailatraiter: null } },
        refetchQueries: [{ query: DEMANDES_A_TRAITER_QUERY, variables: { gestionnaireId: id } }],
      })
    }, 5000)
  }

  const handleCreateEvenement = (values: any) => {
    createEvenement({
      variables: {
        data: { demande_id: demandeId, ...values },
      },
    })
    updateDemande({
      variables: { id: demandeId, data: { datedernierimportmailatraiter: null } },
      refetchQueries: [{ query: DEMANDES_A_TRAITER_QUERY, variables: { gestionnaireId: id } }],
    })
    form.resetFields()
  }

  return (
    <Form form={form} name="demande-drawer" onFinish={handleCreateEvenement}>
      <D4UploadFile onOk={handleOnOk} />
      <Form.Item name="texte" rules={[{ required: true, message: 'Le texte est vide' }]}>
        <TextArea rows={2} />
      </Form.Item>
      <Row justify="space-between" align="top">
        <Form.Item>
          <Button htmlType="submit" loading={loading} type="primary">
            Envoyer
          </Button>
        </Form.Item>
        <Form.Item>
          <GPTButton demandeId={demandeId} />
        </Form.Item>
        <Space>
          <Button onClick={onOpenFilesDrawer}>Fichiers</Button>
          {procedure && (
            <Button type="default" onClick={onOpenProcedure}>
              Procédure
            </Button>
          )}
        </Space>
      </Row>
    </Form>
  )
}
export default DemandeContentEditor
